import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api, type LeagueHub, type SeriesView } from './api'
import { isInPlay } from './inPlay'
import { fixturesAreLive, pace, seriesAreLive } from './pacing'
import { BetLedger } from './components/BetLedger'
import { BookmakerPicker } from './components/BookmakerPicker'
import { Explainer } from './components/Explainer'
import { Term } from './components/Term'
import { VersionBadge } from './components/VersionBadge'
import { FavouritesPage } from './components/FavouritesPage'
import { FixtureCard } from './components/FixtureCard'
import { ModelPage } from './components/ModelPage'
import { DayTabs } from './components/DayTabs'
import { Detail } from './components/Detail'
import { SeriesCard, SeriesLegend } from './components/SeriesCard'
import { ThemeToggle } from './components/ThemeToggle'
import { formatDay, groupByLocalDay, timezoneLabel } from './time'
import { FantasyPage } from './components/fantasy/FantasyPage'
import { TABS, useRoute } from './route'

/** The tournament that gets its own hub. */
const TI2026_LEAGUE_ID = 19719

export default function App() {
  const [route, go] = useRoute()
  const tab = route.tab

  return (
    <div className="min-h-screen">
      {/*
        STICKY, AND ONE ROW OF TABS.

        Five tabs wrapped onto two rows and, with the theme control beside them, the header took
        about 250px of an 852px phone before any content — a quarter of the screen spent on
        navigation, on a page that then runs to nine thousand pixels. It did not stick, so changing
        tab from the bottom of the hub meant scrolling the whole way back up.

        Sticky fixes the second problem. The first is fixed by letting the tab row scroll sideways
        instead of wrapping, which is what the day-tab strip already does — every destination stays
        reachable and the header stops growing as tabs are added. Solid rather than translucent
        deliberately: a blurred header cannot be contrast-checked, because what sits behind it
        changes as the page scrolls.
      */}
      <header className="sticky top-0 z-40 border-b border-line bg-surface">
        <div className="mx-auto max-w-6xl px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
              Dota Oracle
              {/*
                The site's standing caveat, in ONE place.

                It used to be three: an amber bar on the Bet record, a second amber bar 300px below
                it, and a paragraph on the Model page — all making the same argument, all above the
                numbers the reader came for. Repetition does not increase caution, it teaches the eye
                to skip amber boxes, and it takes the situational warnings with it.

                So the status rides on the Beta badge that was already here, where it is reachable
                from every page and shouts at nobody. The Model page still carries the full argument.
              */}
              <Detail
                label="What Beta means here"
                heading={
                  <span className="rounded bg-accent-bg px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-accent">
                    Beta
                  </span>
                }
              >
                <strong className="text-ink">The edges here are unproven.</strong> Our estimates
                carry materially less information than the sharp market&rsquo;s — about half its
                dispersion — and a bet is picked as the widest gap between the two, which lands on
                the longer price nearly every time. Simulated, a forecaster with no miscalibration at
                all reproduces that pattern and still loses 7.2%.
                <span className="mt-2 block border-t border-line pt-2 text-ink-faint">
                  The probabilities are worth reading; treat the suggested bets as a record being
                  built. The Model tab has the full argument and the numbers behind it.
                </span>
              </Detail>
            </h1>
              <p className="truncate text-xs text-ink-faint">
                Calibrated win probabilities for professional Dota 2 · times in {timezoneLabel()}
              </p>
            </div>
            <div className="shrink-0">
              <ThemeToggle />
            </div>
          </div>

          {/* Bled to the screen edges so the row reads as scrollable rather than cut off, and so the
              first and last tabs are not stranded against the padding. */}
          <nav className="-mx-4 mt-2 flex gap-1 overflow-x-auto px-4 text-sm sm:mt-3">
            {TABS.filter((entry) => !entry.unlisted).map((entry) => (
              <TabButton key={entry.id} active={tab === entry.id} onClick={() => go(entry.id)}>
                {entry.label}
              </TabButton>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        {tab === 'hub' ? <TournamentHub leagueId={TI2026_LEAGUE_ID} showExplainer /> : null}
        {tab === 'favourites' ? <FavouritesPage leagueId={TI2026_LEAGUE_ID} /> : null}
        {tab === 'tournaments' ? <Tournaments /> : null}
        {tab === 'schedule' ? <Schedule /> : null}
        {tab === 'bets' ? <Bets /> : null}
        {tab === 'model' ? (
          <ModelPage segments={route.segments} go={(panel) => go('model', panel)} />
        ) : null}
        {tab === 'fantasy' ? (
          <FantasyPage route={route} go={(...segments) => go('fantasy', ...segments)} />
        ) : null}
      </main>

      {/*
        The footer used to quote "65% of matches correctly, and 76% of the ones it was confident
        about" on every page. Those are BACKTEST figures, and they sat one tab away from a Model page
        reporting the live record — two different numbers for the same-sounding claim, with nothing
        saying which was which, on the page a reader is most likely to take as the headline.

        A record belongs where it can be checked, and the Model page exists to be that place. What
        stays here is what the site is and what it is not.
      */}
      <footer className="mx-auto max-w-6xl px-4 pb-10 pt-4 text-xs text-ink-faint">
        {/*
          One line, and it is the only one that has to be here.

          This used to be two paragraphs: one describing the model to a reader who had not asked,
          pointing at a tab already in the navigation; the other claiming every suggestion "shows the
          arithmetic behind it precisely so you can disagree with it", which is not true — a
          boosted-tree ensemble has no honest per-feature breakdown, exactly as the evidence panel
          says when it calls its contents "the inputs, not a breakdown of the answer".

          Replacing an overclaim with a careful explanation of the overclaim is still footer prose
          nobody asked for. The disclaimer earns its place; the essay does not.

          It used to read "Nothing here is betting advice", which was itself an overclaim in the
          other direction: the site names a side, prints a price it would need to beat, and badges
          the ones that clear the bar "advised". Calling that not-advice is a disclaimer contradicted
          by the page it sits under. What is actually true is where the advice comes from, so that is
          what it says.

          Not on the fantasy tab, where it is not merely unnecessary but wrong: nobody is being
          advised to risk anything there, because none of it is real money.
        */}
        {tab === 'fantasy' ? null : (
          <p>All predictions and advisements made on this page are based on an experimental model.</p>
        )}
        <VersionBadge />
      </footer>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      // 44px tall on a phone: Apple's minimum comfortable target, and these are the controls a
      // reader hits first. Relaxed back to the text's own height once there is a pointer, where a
      // 44px row of tabs just looks heavy.
      className={`flex min-h-11 shrink-0 items-center whitespace-nowrap rounded px-3 py-1.5 transition-colors sm:min-h-0 ${
        active ? 'bg-accent-bg text-accent' : 'text-ink-soft hover:bg-surface-raised hover:text-ink'
      }`}
    >
      {children}
    </button>
  )
}

function seriesKey(series: SeriesView): string {
  // Standalone games have no series id, so fall back to the first map's identity.
  return `${series.seriesId ?? series.maps[0]?.matchId ?? series.startTime}`
}

/**
 * Series being played right now, including the gap between games.
 *
 * Strictly disjoint from everything below it: in play here, not yet started below, finished in the
 * day groups. All three read {@link isInPlay}, so they cannot disagree about which bucket a series
 * belongs in — when they did, a match showed up twice or not at all.
 */
function LiveSection({ series, showLeague = false }: { series: SeriesView[]; showLeague?: boolean }) {
  // Not `.filter(isInPlay)`: filter passes (element, index, array), so a bare numeric second
  // parameter would take the INDEX as the time. That shipped once. isInPlay takes a clock object
  // now, so this is a compile error rather than a silent one — the arrow stays for clarity.
  const live = series.filter((s) => isInPlay(s)).sort((a, b) => a.startTime - b.startTime)

  if (live.length === 0) {
    return null
  }

  // Live is the accent plus motion, not red. Red is a lost bet, and a pulsing red header above a
  // column of red losses taught the eye nothing about either. Nothing else on the site pulses, so
  // the movement carries the meaning on its own.
  return (
    <section>
      <SectionHeading
        count={live.length}
        marker={
          <span className="size-1.5 animate-pulse rounded-full bg-live motion-reduce:animate-none" />
        }
        tone="text-live"
      >
        In play
      </SectionHeading>
      <div className="grid gap-3 lg:grid-cols-2">
        {live.map((s) => (
          <SeriesCard key={seriesKey(s)} series={s} showLeague={showLeague} />
        ))}
      </div>
    </section>
  )
}

/**
 * Scheduled matches, grouped by the reader's local day.
 *
 * Kept separate from {@link UpcomingSection} because it draws on a different source: these come
 * from the published schedule and have no maps or score yet, whereas that section works from games
 * that already exist. Its own query too, so a Liquipedia outage empties this band and leaves the
 * rest of the page intact.
 */
function FixtureSection({ leagueId, showTournament = false }: {
  leagueId?: number
  showTournament?: boolean
}) {
  const { data, error, isPending } = useQuery({
    queryKey: ['upcoming', leagueId ?? 'all'],
    queryFn: () => api.upcoming(leagueId),
    // Fast while a lobby is open or a kick-off is close, calm the rest of the day. See pacing.ts.
    refetchInterval: (q) => pace(fixturesAreLive(q.state.data)),
    refetchOnWindowFocus: true,
  })

  // Silent on FAILURE and on empty, but not while still loading. Between tournaments there is
  // genuinely nothing scheduled and an error box for that is noise — whereas rendering nothing
  // during the fetch and then everything after it is the second of the two reflows this page had.
  if (isPending) {
    return (
      <div
        className="h-64 animate-pulse rounded-lg bg-surface motion-reduce:animate-none"
        aria-busy="true"
      />
    )
  }

  if (error || !data || data.length === 0) {
    return null
  }

  const days = groupByLocalDay(data)

  return (
    <section>
      <SectionHeading count={data.length}>Coming up</SectionHeading>
      <div className="space-y-4">
        {days.map((day) => (
          <div key={day.key}>
            <h4 className="mb-2 text-xs text-ink-faint">{formatDay(day.startTime)}</h4>
            <div className="grid gap-3 lg:grid-cols-2">
              {day.items.map((fixture) => (
                <FixtureCard
                  key={fixture.id}
                  fixture={fixture}
                  showTournament={showTournament}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * One heading shape for every band of matches.
 *
 * The hub used three: a red-tinted box for "In play", a teal-tinted box for "Coming up", and bare
 * content under the day tabs — three visual languages for what is, to a reader, one list of matches
 * in three states. The tinted boxes also wrapped cards inside cards, so a match card sat two borders
 * deep and the border stopped meaning anything.
 *
 * State now travels on the card, where it belongs: a live card pulses, an upcoming card carries a
 * countdown, a finished card carries the score. The section above it is a label and a count.
 */
function SectionHeading({
  children,
  count,
  marker,
  tone = 'text-ink-soft',
}: {
  children: React.ReactNode
  count: number
  marker?: React.ReactNode
  tone?: string
}) {
  return (
    <h3 className={`mb-3 flex items-center gap-2 text-sm font-medium ${tone}`}>
      {marker}
      {children}
      <span className="tabular rounded bg-surface-raised px-1.5 py-0.5 text-xs text-ink-faint">
        {count}
      </span>
    </h3>
  )
}

function DayGroups({ series, showLeague = false }: { series: SeriesView[]; showLeague?: boolean }) {
  // Live series are rendered by LiveSection; including them here too is what made the same match
  // appear twice on one screen.
  const played = series.filter((s) => !isInPlay(s))

  return (
    <DayTabs
      items={played}
      emptyLabel="No completed series on this day."
      render={(items) => (
        <>
          <SeriesLegend />
          {/* A grid, deliberately, and not CSS columns.

              Columns pack tighter — a grid rows to its tallest cell, so one card carrying a
              bookmaker block leaves dead space beside a shorter neighbour. But columns flow DOWN
              one column and then back to the top of the next, so reaching the ninth match means
              scrolling to the bottom and back up again. On a list ordered by recency that is the
              wrong trade: the newest matches must be readable across the top, left to right,
              without travelling. Whitespace is cheaper than making someone scroll twice. */}
          <div className="grid gap-3 lg:grid-cols-2">
            {items
              .slice()
              .sort((a, b) => b.startTime - a.startTime)
              .map((s) => (
                <SeriesCard key={seriesKey(s)} series={s} showLeague={showLeague} />
              ))}
          </div>
        </>
      )}
    />
  )
}

/**
 * One tournament in full.
 *
 * Parameterised rather than pinned to TI: the same view serves every league we track, and having
 * two near-identical components drift apart is a certainty rather than a risk.
 */
function TournamentHub({ leagueId, showExplainer = false }: {
  leagueId: number
  showExplainer?: boolean
}) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['hub', leagueId],
    queryFn: () => api.leagueHub(leagueId),
    refetchInterval: (q) => pace(seriesAreLive(q.state.data?.series)),
    refetchOnWindowFocus: true,
  })

  if (isPending) return <Loading />
  if (error) {
    return (
      <Failed
        error={error}
        what="this tournament's matches"
        retry={() => void refetch()}
        stillWorks="Upcoming fixtures, the bet record and the model page load separately and may still be available."
      />
    )
  }

  /*
   * ORDER: what can still be acted on, then what is happening, then the record.
   *
   * This page used to open with three accuracy percentages, a paragraph explaining why one of them
   * flatters the model, and a 250px panel teaching decimal odds — so the first upcoming fixture, the
   * only thing on the page a reader can do anything about, began around 1,400px down on a phone.
   * The retrospective outranked the live product.
   *
   * The record has not been hidden, only sized correctly: it is a strip under the title rather than
   * the headline, and each figure is weighted by the evidence behind it. The explainer follows the
   * fixtures, where its "1.79 or better" worked example sits beside a real one instead of preceding
   * anything it could explain.
   */
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">{data.league.name}</h2>
        <RecordStrip hub={data} />
      </div>

      <LiveSection series={data.series} />
      <FixtureSection leagueId={leagueId} />

      {showExplainer ? <Explainer /> : null}

      {data.series.length === 0 ? (
        <Empty>No matches played in this tournament yet.</Empty>
      ) : (
        <DayGroups series={data.series} />
      )}
    </div>
  )
}

/**
 * The tournament's record, as one line rather than three headline cards.
 *
 * **Weighted by evidence, not annotated with it.** The three figures used to sit at identical size
 * with "small sample" whispered under whichever deserved it — so 71% off seven maps was drawn as
 * loudly as 69% off thirty-nine, and being the largest number on the page, read as the best news on
 * it. Anything under thirty is now rendered at the weight it has earned, which is the same
 * information without the emphasis being a lie.
 *
 * Why series accuracy flatters the model is a real caveat and now sits behind the figure it
 * qualifies, rather than as a paragraph the reader must pass to reach the matches.
 */
function RecordStrip({ hub }: { hub: LeagueHub }) {
  // Shares the model page's query key, so this is a cache read rather than another request.
  const { data: model } = useQuery({ queryKey: ['model'], queryFn: api.model })
  const entries: { label: string; record: { correct: number; total: number; rate: number } | null }[] =
    [
      { label: 'Series', record: hub.seriesAccuracy },
      { label: 'Maps, pre-draft', record: hub.mapAccuracy?.preDraft ?? hub.accuracy },
      { label: 'Maps, post-draft', record: hub.mapAccuracy?.postDraft ?? null },
    ]

  const shown = entries.filter((entry) => entry.record !== null)
  if (shown.length === 0) {
    return null
  }

  return (
    <p className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm text-ink-soft">
      {/*
        The line names its subject before its figures — whose record this is, and which version's,
        because a promotion changes them. What follows used to be a per-model breakdown as well, so
        one hub line carried five versions' figures beside the champion's own; that split now lives
        on the model page, where a reader who wants it has room for it.
      */}
      <span className="text-ink-faint">
        <a href="#/model" className="underline decoration-dotted underline-offset-2 hover:text-ink">
          Dota Oracle{model?.modelVersion ? ` (${model.modelVersion})` : ''}
        </a>{' '}
        predictions:
      </span>
      {shown.map(({ label, record }) => {
        const thin = record!.total < 30
        return (
          <span key={label} className={thin ? 'text-ink-faint' : ''}>
            <span className={`tabular ${thin ? '' : 'text-ink'}`}>
              {(record!.rate * 100).toFixed(0)}%
            </span>{' '}
            {label.toLowerCase()}{' '}
            <span className="tabular text-ink-faint">
              ({record!.correct}/{record!.total})
            </span>
          </span>
        )
      })}
      {hub.seriesAccuracy && hub.accuracy ? (
        <Detail
          label="Why the series figure is higher"
          className="text-ink-faint"
          heading={<span>why these differ</span>}
        >
          A series gives the stronger side two chances out of three, so the format widens the edge on
          its own — the series number is higher <em>by construction</em> and is not a better measure
          of the model.
          {hub.mapAccuracy?.postDraft
            ? ' Post-draft counts only the maps where we saw all ten heroes before the horn.'
            : ''}
          <span className="mt-2 block border-t border-line pt-2 text-ink-faint">
            Accuracy is also not the measure that decides whether a price is worth taking — that is
            calibration, on the Model tab.
          </span>
        </Detail>
      ) : null}
    </p>
  )
}

/**
 * Every tournament we hold matches for, and one of them opened in full.
 *
 * The site could previously only show TI or a rolling five-day window, so a completed tournament was
 * unreachable even though its matches and predictions were all in the database.
 */
function Tournaments() {
  const [selected, setSelected] = useState<number | null>(null)
  // Wrapped rather than passed by reference: api.leagues now takes an optional search term, and a
  // bare reference would hand react-query's context object to it as that term.
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['leagues'],
    queryFn: () => api.leagues(),
  })

  if (isPending) return <Loading />
  if (error) {
    return <Failed error={error} what="the tournament list" retry={() => void refetch()} />
  }

  if (selected !== null) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="text-xs text-ink-faint hover:text-ink"
        >
          ← All tournaments
        </button>
        <TournamentHub leagueId={selected} />
      </div>
    )
  }

  // Only leagues with matches: an empty tournament page is a dead end, and the league table carries
  // plenty of entries we have never ingested a game for.
  const withMatches = data.filter((league) => league.matchCount > 0)

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">Tournaments</h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-soft">
          {withMatches.length} {withMatches.length === 1 ? 'event' : 'events'} we hold matches for.
          Opening one shows its full record — every
          series, what we said before it, and any prices we captured.
        </p>
      </div>

      {/*
        Featured events are separated rather than merely sorted first.
        
        The list was one undifferentiated grid ordered by a flag the reader cannot see, so a major
        event and a play-in nobody watched were the same object in the same row. The count of matches
        is the only measure of substance available here — there is no per-league record in the
        payload — so it does the work of ranking within each group.
      */}
      {[
        { label: 'Featured', leagues: withMatches.filter((l) => l.isFeatured) },
        { label: 'Everything else', leagues: withMatches.filter((l) => !l.isFeatured) },
      ]
        .filter((group) => group.leagues.length > 0)
        .map((group) => (
          <section key={group.label}>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-medium text-ink-soft">
              {group.label}
              <span className="tabular rounded bg-surface-raised px-1.5 py-0.5 text-xs text-ink-faint">
                {group.leagues.length}
              </span>
            </h3>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {[...group.leagues]
                .sort((a, b) => b.matchCount - a.matchCount)
                .map((league) => (
                  <button
                    key={league.leagueId}
                    type="button"
                    onClick={() => setSelected(league.leagueId)}
                    className="flex flex-col justify-between gap-2 rounded-lg border border-line bg-surface p-3 text-left transition-colors hover:border-accent-edge"
                  >
                    <span className="min-w-0 text-sm">
                      {league.name ?? `League ${league.leagueId}`}
                    </span>
                    <span className="tabular text-xs text-ink-faint">
                      {league.matchCount} {league.matchCount === 1 ? 'match' : 'matches'} · tier{' '}
                      {league.tierRank}
                    </span>
                  </button>
                ))}
            </div>
          </section>
        ))}
    </div>
  )
}

function Schedule() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['schedule'],
    queryFn: () => api.schedule(),
    refetchInterval: (q) => pace(seriesAreLive(q.state.data?.series)),
    refetchOnWindowFocus: true,
  })

  if (isPending) return <Loading />
  if (error) {
    return (
      <Failed
        error={error}
        what="recent and live matches"
        retry={() => void refetch()}
        stillWorks="The scheduled fixtures below come from a separate source and may still load."
      />
    )
  }

  // Note the early return is gone: scheduled fixtures live in their own query, so bailing out on an
  // empty match list would hide next week's schedule during a quiet stretch between tournaments.
  return (
    <div className="space-y-6">
      {/* This page had no heading at all — it opened straight into a live band, so a reader arriving
          from the tab had no idea what set of matches they were looking at or how far back it went.
          The window is a real constraint worth stating: production keeps a 36-hour lookback, so
          "everything" here means recent, not all-time. */}
      <div>
        <h2 className="text-2xl font-semibold">All matches</h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-soft">
          Every professional match we are tracking right now, across all tournaments — recent
          results, anything in play, and what is scheduled next. For a single event with its record
          attached, use Tournaments.
        </p>
      </div>

      <LiveSection series={data.series} showLeague />
      <FixtureSection showTournament />
      {data.series.length === 0 ? (
        <Empty>No professional matches played in the current window.</Empty>
      ) : (
        <DayGroups series={data.series} showLeague />
      )}
    </div>
  )
}

/**
 * The betting record, for the book the reader actually holds an account with.
 *
 * The book is a choice, not a constant. This page was pinned to Danske Spil, the book the site was
 * conceived around — and the odds provider turns out not to price Dota for it, so the page showed
 * the handful of prices that had been entered by hand and none of the ninety-odd books the capture
 * brings in. The data had been there for hours; the filter could not see it.
 *
 * Undefined means "whatever the server considers best covered", which is how a first visit avoids
 * landing on an empty page again.
 */
/**
 * Pre-draft or post-draft, for the whole page.
 *
 * <b>These are two different questions and only one of them is a record.</b> Pre-draft compares our
 * estimate against the book's when both were formed from the same information, at a moment somebody
 * could have acted. Post-draft compares our sharper number against a price the book set before it
 * could see the heroes — which is the gap this site was built to find, and also the reason the
 * figures flatter: every capture so far was struck pre-draft, so on a settled row the edge is a
 * counterfactual rather than a bet anybody could have placed.
 *
 * That caveat is stated rather than left implied, because a page which quietly switched basis would
 * be reporting two different records under one heading.
 */
function DraftBasis({
  on,
  onChange,
  counted,
}: {
  on: boolean
  onChange: (next: boolean) => void
  counted: number
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="inline-flex overflow-hidden rounded border border-line">
        {[
          { id: false, label: 'Pre-draft' },
          { id: true, label: 'Post-draft' },
        ].map((option) => (
          <button
            key={String(option.id)}
            type="button"
            aria-pressed={on === option.id}
            onClick={() => onChange(option.id)}
            className={`px-3 py-1 text-xs transition-colors ${
              on === option.id
                ? 'bg-accent-bg font-medium text-accent'
                : 'text-ink-soft hover:text-ink'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <span className="max-w-2xl text-xs text-ink-faint">
        {on ? (
          <>
            Graded against our number <strong className="text-ink">after</strong> the heroes locked,
            on prices struck before them. Every capture so far was pre-draft, so a settled row here
            is what we <em>would</em> have had — not a bet anybody could have placed, unless the
            market was still open between the draft and the horn.
          </>
        ) : (
          <>
            Graded against the number that was live when each price was bought — what anybody could
            actually have bet into. {counted} rows.
          </>
        )}
      </span>
    </div>
  )
}

function Bets() {
  const [bookmaker, setBookmaker] = useState<string | undefined>(undefined)

  /*
   * Which information the whole page bets on.
   *
   * Off is the record: our estimate and the book's, both formed before the heroes were known, at a
   * moment the market was open. On is the opportunity this site exists for — our post-draft number
   * against a price the book struck before it could see the draft.
   *
   * Default off, because only one of the two is a record and a page that opens on the flattering
   * view is a page that flatters. See the counterfactual note below.
   */
  const [draftAware, setDraftAware] = useState(false)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bets', bookmaker ?? 'default', draftAware ? 'post' : 'pre'],
    queryFn: () => api.bets(undefined, bookmaker, draftAware),
    // Matches go live and settle while a page sits open, and a stale suggestion is one someone may
    // act on after the market has closed — so this follows the games rather than a fixed clock.
    refetchInterval: (q) => pace(q.state.data?.bets?.some((b) => b.isLive) ?? false),
    refetchOnWindowFocus: true,
  })

  if (isPending) return <Loading />
  if (error) {
    return (
      <Failed
        error={error}
        what="the bet record"
        retry={() => void refetch()}
        stillWorks="Match probabilities do not depend on captured prices and are still on the tournament tab."
      />
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">Bet record</h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-soft">
          A bet is suggested when the <Term id="edge" define /> over the price on offer is at least{' '}
          {(data.minEdge * 100).toFixed(0)} points — the same thing as the price beating our{' '}
          <Term id="break-even" define /> by that much. The{' '}
          <Term id="odds" define /> are the last seen before kick-off, so a{' '}
          <Term id="closing-line" define /> is what most rows carry. Unstarted matches are listed
          first, because those are the only ones you can still act on.
        </p>
      </div>

      <DraftBasis on={draftAware} onChange={setDraftAware} counted={data.bets.length} />

      {data.availableBookmakers?.length > 1 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <BookmakerPicker
            books={data.availableBookmakers}
            selected={data.bookmaker}
            onSelect={setBookmaker}
          />
          <span className="max-w-2xl text-xs text-ink-faint">
            {data.availableBookmakers.length} books captured. Prices differ between them, and so does
            the edge — that difference is the easiest one on this site to collect.
          </span>
        </div>
      ) : null}

      {/*
        There used to be an amber warning here whenever the selected book was the sharp reference —
        which is the default, so it fired on every visit. It is gone.

        The point of this site is to beat the market; a standing notice that the market is hard to
        beat is not information, it is a disclaimer that trains the reader to skip amber boxes,
        including the ones that mean something. The sharp comparison still runs where it can, and
        still speaks up per row when a price genuinely disagrees — which is the case worth seeing,
        and mostly worth seeing because it catches our own bugs.
      */}
      {!data.sharpCheckActive ? (
        <p className="text-xs text-ink-faint">
          Prices from {data.bookmakerName}, which is also the reference other books are checked against —
          so there is nothing to cross-check these rows with. It is not licensed in Denmark.
        </p>
      ) : null}

      <BetLedger record={data} />
    </div>
  )
}

/**
 * Placeholders at the shape of what is coming, rather than a word in the middle of an empty page.
 *
 * Two reflows used to be visible on every load. First the centred "Loading…" gave way to a full
 * page, moving everything; then the fixtures band — its own query, so it resolves separately —
 * arrived and shoved the day tabs down again. On mobile data the gap between the two is long enough
 * to start reading in.
 *
 * Sizing matters more than the shimmer: a skeleton that is not the height of the thing it stands in
 * for just relocates the jump. These are measured against a real card.
 */
function Loading({ cards = 4 }: { cards?: number }) {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading…</span>
      <div>
        <div className="h-8 w-64 max-w-full animate-pulse rounded bg-surface-raised motion-reduce:animate-none" />
        <div className="mt-2 h-4 w-80 max-w-full animate-pulse rounded bg-surface-raised motion-reduce:animate-none" />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {Array.from({ length: cards }, (_, index) => (
          <div key={index} className="h-44 animate-pulse rounded-lg bg-surface motion-reduce:animate-none" />
        ))}
      </div>
    </div>
  )
}

/**
 * A failure that says what broke, what still works, and how to try again.
 *
 * It was one red line quoting an exception message. That is the wrong shape for this site
 * specifically: the pages draw on four independent upstreams — Liquipedia for the schedule, OpenDota
 * for results, Steam for live games, an odds provider for prices — so PARTIAL failure is the normal
 * case rather than the exception, and "Could not load: 500" tells a reader neither which of those is
 * down nor that the other three still have something for them.
 *
 * `retry` is offered rather than a page reload because each panel owns its own query; re-fetching
 * one does not discard the three that worked.
 */
function Failed({
  error,
  what = 'this page',
  retry,
  stillWorks,
}: {
  error: unknown
  /** What could not be loaded, in the reader's terms. */
  what?: string
  retry?: () => void
  /** Where to go for something that is still available. */
  stillWorks?: React.ReactNode
}) {
  const message = error instanceof Error ? error.message : 'unknown error'

  return (
    <div className="rounded-lg border border-danger-edge bg-danger-bg px-4 py-3 text-sm">
      <p className="font-medium text-danger">Could not load {what}.</p>
      {stillWorks ? <p className="mt-1 text-ink-soft">{stillWorks}</p> : null}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {retry ? (
          <button
            type="button"
            onClick={retry}
            className="rounded border border-danger-edge px-2 py-1 text-xs text-danger transition-colors hover:bg-surface"
          >
            Try again
          </button>
        ) : null}
        <span className="text-xs text-ink-faint">{message}</span>
      </div>
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded border border-line bg-surface px-4 py-8 text-center text-sm text-ink-soft">
      {children}
    </p>
  )
}
